/**
 * The faces the Coast app sets itself and its views in (docs/views.md#4-layouts): the system's, the default, or Geist,
 * whose files the view's server serves at `/fonts/<file>` (build.mjs copies them from `design/assets/fonts`, with their
 * SIL Open Font License). A page gets the face as `--coast-font` and `--coast-font-mono`, and as `coast.font`.
 */
import typeScale from "../generated/type.json";

export const FONT_FACES = {
  system: {
    family: '-apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", sans-serif',
    mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    files: [],
  },
  geist: {
    family: '"Geist", -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", sans-serif',
    mono: '"Geist Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    files: [
      { family: "Geist", file: "Geist-Variable.woff2" },
      { family: "Geist Mono", file: "GeistMono-Variable.woff2" },
    ],
  },
} as const satisfies Record<
  string,
  { family: string; mono: string; files: readonly { family: string; file: string }[] }
>;
export type Face = keyof typeof FONT_FACES;
export const isFace = (value: unknown): value is Face => typeof value === "string" && value in FONT_FACES;

/** The font as a page reads it, `coast.font`: the face and its stacks. */
export const fontOf = (face: Face) => ({ face, family: FONT_FACES[face].family, mono: FONT_FACES[face].mono });

/**
 * The CSS a page starts with for `face`: its files from `server`, the variables, and the rule that sets it on the page
 * and its controls over any `font-family` of the view's, unless its manifest says `font: "own"`
 * (docs/spec/view-format.md#23-how-it-shows, M10).
 */
export function fontStyle(face: Face, server: string, own: boolean): string {
  const { family, mono, files } = FONT_FACES[face];
  const faces = files.map(
    (entry) =>
      `@font-face{font-family:"${entry.family}";src:url("${server}/fonts/${entry.file}") format("woff2");font-weight:100 900;font-display:swap}`,
  );
  const variables = `:root{--coast-font:${family};--coast-font-mono:${mono}}`;
  const rule = own
    ? ""
    : "*,*::before,*::after{font-family:var(--coast-font)!important}code,kbd,pre,samp,code *,pre *{font-family:var(--coast-font-mono)!important}";
  return [...faces, variables, rule].join("");
}

/**
 * The roles of Coast's type scale (design/tokens.json, docs/views.md#4-layouts), each one of the platform's text styles:
 * a page's `--coast-size-<role>` in CSS pixels, which are points in the Coast app's web views.
 */
export type TextRole = keyof typeof typeScale.scale;
export type TextSizes = Record<TextRole, number>;
const ROLES = Object.keys(typeScale.scale) as TextRole[];
/** What a page sizes its text by: each role's size, and `scale`, the body's against the platform's at its default. */
export interface PageText {
  sizes: TextSizes;
  scale: number;
}
/**
 * The sizes Apple gives the roles at the default text size: the Mac's for a page in the desktop layout, iPhone's for the
 * rest, for a page no Coast app gives its own, such as a render's.
 */
export function defaultText(platform: "ios" | "mac"): PageText {
  return {
    sizes: Object.fromEntries(ROLES.map((role) => [role, typeScale.scale[role][platform]])) as TextSizes,
    scale: 1,
  };
}
/**
 * Text sizes as the Coast app sends them (`setTextSizes`), checked: every role a number of points between 6 and 120,
 * which Dynamic Type's largest accessibility sizes stay within, and the scale between a quarter and eight.
 */
export function pageTextOf(value: unknown): PageText | null {
  if (!value || typeof value !== "object") return null;
  const { sizes, scale } = value as { sizes?: Record<string, unknown>; scale?: unknown };
  if (!sizes || typeof sizes !== "object" || typeof scale !== "number" || !(scale >= 0.25 && scale <= 8)) return null;
  const checked: Partial<TextSizes> = {};
  for (const role of ROLES) {
    const size = sizes[role];
    if (typeof size !== "number" || !(size >= 6 && size <= 120)) return null;
    checked[role] = Math.round(size * 100) / 100;
  }
  return { sizes: checked as TextSizes, scale: Math.round(scale * 1000) / 1000 };
}
/**
 * A page's text sizes as CSS, `--coast-size-<role>` and `--coast-text-scale`, on `:root:root`, which outranks the stock
 * style's own defaults on `:root` though this rule comes before it.
 */
export function textStyle({ sizes, scale }: PageText): string {
  return `:root:root{${ROLES.map((role) => `--coast-size-${role}:${sizes[role]}px;`).join("")}--coast-text-scale:${scale}}`;
}
